import { useMemo } from "react";
import { useMutation } from "@tanstack/react-query";

import {
  generateId,
  useJournalEntriesData,
  useOwnerId,
  type PlantJournalEntry,
} from "~/state/sync";
import { mk, type MutationVars } from "~/state/mutations";
import type { JournalEntryKind } from "~/api/types";

export function usePlantJournalEntries(plantId: string | undefined) {
  const { data } = useJournalEntriesData();
  return useMemo(() => {
    if (!plantId) return [] as PlantJournalEntry[];
    return (data ?? [])
      .filter((entry) => entry.plant_id === plantId)
      .slice()
      .sort((a, b) => b.occurred_at.localeCompare(a.occurred_at));
  }, [data, plantId]);
}

export function useJournalActions() {
  const userId = useOwnerId();
  const createM = useMutation<unknown, Error, MutationVars["journalCreate"]>({
    mutationKey: [...mk.journalCreate],
  });
  const removeM = useMutation<unknown, Error, MutationVars["journalRemove"]>({
    mutationKey: [...mk.journalRemove],
  });

  function requireUser() {
    if (!userId) throw new Error("You must be signed in.");
    return userId;
  }

  function create(
    plantId: string,
    input: { kind: JournalEntryKind; body?: string | null },
  ) {
    const uid = requireUser();
    const id = generateId();
    const now = new Date().toISOString();
    const row: PlantJournalEntry = {
      id,
      plant_id: plantId,
      owner_id: uid,
      kind: input.kind,
      body: input.body?.trim() || null,
      occurred_at: now,
      created_at: now,
      updated_at: now,
      deleted: false,
    };
    createM.mutate({ userId: uid, row });
    return id;
  }

  function remove(entryId: string) {
    const uid = requireUser();
    removeM.mutate({ userId: uid, id: entryId });
  }

  return { create, remove };
}
